import { useSyncExternalStore } from 'react';

/**
 * Recettes en cuisson (US-35) : « je fais un plat et son accompagnement en
 * même temps ».
 *
 * Rien à ajouter en base : `timers.ts` est **déjà** une liste globale
 * indépendante de la recette, et le mode cuisine mémorise déjà son avancement
 * par recette (`mlc:mode-cuisine:<id>`, US-08). Ce qui manquait, c'était de
 * savoir **quelles** recettes sont en cours, pour pouvoir passer de l'une à
 * l'autre sans repasser par la bibliothèque.
 *
 * `localStorage` comme les minuteurs, et pour la même raison (§3.3) : une
 * cuisson ne doit pas être perdue parce que l'onglet a été fermé par erreur.
 * `sessionStorage` conviendrait au seul avancement, pas à la liste elle-même.
 *
 * `useSyncExternalStore` plutôt qu'un état React : la liste est modifiée
 * depuis plusieurs écrans et depuis un autre onglet, exactement ce que cette
 * API décrit — même choix que pour l'URL (`router.tsx`) et les minuteurs.
 */

const STORAGE_KEY = 'mlc:recettes-en-cuisson';
const CHANGE_EVENT = 'mlc:recettes-en-cuisson:change';

export interface ActiveRecipe {
  id: string;
  title: string;
  /** Horodatage epoch (ms) du début de la cuisson, pour ordonner la liste. */
  started_at: number;
}

function read(): ActiveRecipe[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry): entry is ActiveRecipe =>
      typeof entry === 'object' && entry !== null
      && typeof (entry as ActiveRecipe).id === 'string'
      && typeof (entry as ActiveRecipe).title === 'string');
  } catch {
    // Stockage refusé ou contenu illisible : on repart d'une liste vide
    // plutôt que d'empêcher de cuisiner.
    return [];
  }
}

function write(recipes: readonly ActiveRecipe[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recipes));
  } catch {
    // Comme ci-dessus : l'échec d'écriture ne doit rien interrompre.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * Instantané stable : `useSyncExternalStore` compare par identité, et
 * relire le JSON à chaque rendu produirait un nouveau tableau à chaque fois —
 * donc une boucle de rendus.
 */
let snapshot: ActiveRecipe[] = [];
let snapshotRaw: string | null = null;

function getSnapshot(): ActiveRecipe[] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    raw = null;
  }
  if (raw !== snapshotRaw) {
    snapshotRaw = raw;
    snapshot = read();
  }
  return snapshot;
}

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, onStoreChange);
  // `storage` : la même cuisson suivie depuis deux onglets, cas réel quand on
  // ouvre une recette sur la tablette et une autre sur le téléphone.
  window.addEventListener('storage', onStoreChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
}

export function useActiveRecipes(): ActiveRecipe[] {
  return useSyncExternalStore(subscribe, getSnapshot, () => []);
}

/** Épingle une recette. Sans effet si elle l'est déjà : rouvrir le mode cuisine ne remet pas le compteur à zéro. */
export function pinRecipe(id: string, title: string): void {
  const current = read();
  if (current.some((entry) => entry.id === id)) return;
  write([...current, { id, title, started_at: Date.now() }]);
}

export function unpinRecipe(id: string): void {
  write(read().filter((entry) => entry.id !== id));
}

export function isPinned(id: string): boolean {
  return read().some((entry) => entry.id === id);
}
